import {PhoneNumber} from 'ApplicationEntry/models/PhoneNumber';
import {ZipCode} from 'ApplicationEntry/models/ZipCode';
import {StringUnion} from 'Core/models/EmptyString';
import {createAction} from 'Shared/utilities/actionCreator';
import {reverseEnum} from 'Shared/utilities/enumUtility';
import * as P from '../models/PrimaryBeneficiary';
import {PrimaryBeneficiary} from '../models/PrimaryBeneficiary';

export enum PrimaryBeneficiaryActionType {
  CHANGE_FIRST_NAME = 'primaryBeneficiary/CHANGE_FIRST_NAME',
  CHANGE_MIDDLE_NAME = 'primaryBeneficiary/CHANGE_MIDDLE_NAME',
  CHANGE_LAST_NAME = 'primaryBeneficiary/CHANGE_LAST_NAME',
  CHANGE_RELATIONSHIP = 'primaryBeneficiary/CHANGE_RELATIONSHIP',
  CHANGE_PERCENT = 'primaryBeneficiary/CHANGE_PERCENT',
  CHANGE_SOCIAL_SECURITY_NUMBER = 'primaryBeneficiary/CHANGE_SOCIAL_SECURITY_NUMBER',
  CHANGE_DATE_OF_BIRTH = 'primaryBeneficiary/CHANGE_DATE_OF_BIRTH',
  CHANGE_PHONE = 'primaryBeneficiary/CHANGE_PHONE',
  CHANGE_ADDRESS = 'primaryBeneficiary/CHANGE_ADDRESS',
  CHANGE_ADDRESS_LINE2 = 'primaryBeneficiary/CHANGE_ADDRESS_LINE2',
  CHANGE_CITY = 'primaryBeneficiary/CHANGE_CITY',
  CHANGE_STATE = 'primaryBeneficiary/CHANGE_STATE',
  CHANGE_ZIP = 'primaryBeneficiary/CHANGE_ZIP',
  UPDATE_PRIMARY_BENEFICIARY = 'primaryBeneficiary/UPDATE_PRIMARY_BENEFICIARY',
  CHANGE_SIGNATURE_DATE = 'primaryBeneficiary/CHANGE_SIGNATURE_DATE',
}

export const changeFirstName = (firstName: string) =>
  createAction(PrimaryBeneficiaryActionType.CHANGE_FIRST_NAME, {firstName});
export const changeMiddleName = (middleName: string) =>
  createAction(PrimaryBeneficiaryActionType.CHANGE_MIDDLE_NAME, {middleName});
export const changeLastName = (lastName: string) =>
  createAction(PrimaryBeneficiaryActionType.CHANGE_LAST_NAME, {lastName});
export const changeRelationship = (relationship: string) =>
  createAction(PrimaryBeneficiaryActionType.CHANGE_RELATIONSHIP, {relationship});
export const changePercent = (percent: number) => createAction(PrimaryBeneficiaryActionType.CHANGE_PERCENT, {percent});
export const changeSSN = (socialSecurityNumber: string) =>
  createAction(PrimaryBeneficiaryActionType.CHANGE_SOCIAL_SECURITY_NUMBER, {socialSecurityNumber});
export const changeDateOfBirth = (dateOfBirth: Date | '') =>
  createAction(PrimaryBeneficiaryActionType.CHANGE_DATE_OF_BIRTH, {dateOfBirth});
export const changePhone = (phone: PhoneNumber) => createAction(PrimaryBeneficiaryActionType.CHANGE_PHONE, {phone});
export const changeAddress = (address: string) => createAction(PrimaryBeneficiaryActionType.CHANGE_ADDRESS, {address});
export const changeAddressLine2 = (addressLine2: string) =>
  createAction(PrimaryBeneficiaryActionType.CHANGE_ADDRESS_LINE2, {addressLine2});
export const changeCity = (city: string) => createAction(PrimaryBeneficiaryActionType.CHANGE_CITY, {city});
export const changeState = (state: string) => createAction(PrimaryBeneficiaryActionType.CHANGE_STATE, {state});
export const changeZip = (zip: ZipCode) => createAction(PrimaryBeneficiaryActionType.CHANGE_ZIP, {zip});
export const changeSignatureDate = (signatureDate: Date | '') => createAction(PrimaryBeneficiaryActionType.CHANGE_SIGNATURE_DATE, {signatureDate});
export const updatePrimaryBeneficiaryValues = (
  firstName: StringUnion,
  middleName: StringUnion,
  lastName: StringUnion,
  relationship: StringUnion,
  percent: number,
  socialSecurityNumber: string,
  dateOfBirth: Date | '',
  phone: PhoneNumber,
  address: StringUnion,
  addressLine2: StringUnion,
  city: StringUnion,
  state: StringUnion,
  zip: ZipCode,
  occSmokerCode: StringUnion,
  signatureDate: Date | '',
) =>
  createAction(PrimaryBeneficiaryActionType.UPDATE_PRIMARY_BENEFICIARY, {
    firstName,
    middleName,
    lastName,
    relationship,
    percent,
    socialSecurityNumber,
    dateOfBirth,
    phone,
    address,
    addressLine2,
    city,
    state,
    zip,
    occSmokerCode,
    signatureDate,
  });

export type PrimaryBeneficiaryAction =
  | ReturnType<typeof changeFirstName>
  | ReturnType<typeof changeMiddleName>
  | ReturnType<typeof changeLastName>
  | ReturnType<typeof changeRelationship>
  | ReturnType<typeof changePercent>
  | ReturnType<typeof changeSSN>
  | ReturnType<typeof changeDateOfBirth>
  | ReturnType<typeof changePhone>
  | ReturnType<typeof changeAddress>
  | ReturnType<typeof changeAddressLine2>
  | ReturnType<typeof changeCity>
  | ReturnType<typeof changeState>
  | ReturnType<typeof changeZip>
  | ReturnType<typeof updatePrimaryBeneficiaryValues>
  | ReturnType<typeof changeSignatureDate>;

export const isPrimaryBeneficiaryAction = (action: any): action is PrimaryBeneficiaryAction =>
  action.type in reverseEnum(PrimaryBeneficiaryActionType);

type PrimaryBeneficiaryState = PrimaryBeneficiary;
const DEFAULT_STATE: PrimaryBeneficiaryState = P.EmptyPrimaryBeneficiary;

export default (state: PrimaryBeneficiaryState = DEFAULT_STATE, action: PrimaryBeneficiaryAction) => {
  switch (action.type) {
    case PrimaryBeneficiaryActionType.CHANGE_FIRST_NAME:
      return P.changeFirstName(action.payload.firstName)(state);
    case PrimaryBeneficiaryActionType.CHANGE_MIDDLE_NAME:
      return P.changeMiddleName(action.payload.middleName)(state);
    case PrimaryBeneficiaryActionType.CHANGE_LAST_NAME:
      return P.changeLastName(action.payload.lastName)(state);
    case PrimaryBeneficiaryActionType.CHANGE_RELATIONSHIP:
      return P.changeRelationship(action.payload.relationship)(state);
    case PrimaryBeneficiaryActionType.CHANGE_PERCENT:
      return P.changePercent(action.payload.percent)(state);
    case PrimaryBeneficiaryActionType.CHANGE_SOCIAL_SECURITY_NUMBER:
      return P.changeSSN(action.payload.socialSecurityNumber)(state);
    case PrimaryBeneficiaryActionType.CHANGE_DATE_OF_BIRTH:
      return P.changeDOB(action.payload.dateOfBirth)(state);
    case PrimaryBeneficiaryActionType.CHANGE_PHONE:
      return P.changePhone(action.payload.phone)(state);
    case PrimaryBeneficiaryActionType.CHANGE_ADDRESS:
      return P.changeAddress(action.payload.address)(state);
    case PrimaryBeneficiaryActionType.CHANGE_ADDRESS_LINE2:
      return P.changeAddress(action.payload.addressLine2)(state);
    case PrimaryBeneficiaryActionType.CHANGE_CITY:
      return P.changeCity(action.payload.city)(state);
    case PrimaryBeneficiaryActionType.CHANGE_STATE:
      return P.changeState(action.payload.state)(state);
    case PrimaryBeneficiaryActionType.CHANGE_ZIP:
      return P.changeZip(action.payload.zip)(state);
    case PrimaryBeneficiaryActionType.CHANGE_SIGNATURE_DATE:
      return P.changeSignatureDate(action.payload.signatureDate)(state);
    case PrimaryBeneficiaryActionType.UPDATE_PRIMARY_BENEFICIARY:
      return P.updatePrimaryBeneficiaryValues(
        action.payload.firstName,
        action.payload.middleName,
        action.payload.lastName,
        action.payload.relationship,
        action.payload.percent,
        action.payload.socialSecurityNumber,
        action.payload.dateOfBirth,
        action.payload.phone,
        action.payload.address,
        action.payload.addressLine2,
        action.payload.city,
        action.payload.state,
        action.payload.zip,
        action.payload.occSmokerCode,
        action.payload.signatureDate,
      )();
    default:
      return state;
  }
};
